/**
 * TopBar Component
 * Fixed header bar spanning full width with logo and page title.
 * @packageDocumentation
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PipelineNetworkLogo } from '@/components/icons';
import { Badge, ChangeHistoryDialog } from '@/components/ui';

const isNotProd = import.meta.env.MODE !== 'production';

/**
 * Props for the TopBar component.
 */
export interface TopBarProps {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Fixed top bar component with logo and page title.
 *
 * @param { TopBarProps } props Component props.
 * @returns { JSX.Element } The top bar element.
 */
export const TopBar = ({ className }: TopBarProps) => {
  const { t } = useTranslation();
  const pageTitle = usePageTitle();
  const { titleAddon } = useUIStore();
  const [changeHistoryOpen, setChangeHistoryOpen] = useState(false);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40 flex h-16 items-center border-b border-[#e5e7eb] bg-white dark:border-[rgba(255,255,255,0.06)] dark:bg-[#111827]',
        className,
      )}
    >
      {/* Logo section — same width as sidebar (w-72) */}
      <div className="flex h-full w-72 shrink-0 items-center gap-3 pl-10 pr-5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80">
          <PipelineNetworkLogo size={22} color="#ffffff" />
        </div>
        <div className="flex items-baseline gap-2">
          <h1 className="shrink-0 whitespace-nowrap text-[25px] font-semibold tracking-tight text-foreground">
            {t('appName')}
          </h1>
          <button
            type="button"
            onClick={() => setChangeHistoryOpen(true)}
            aria-label={t('topbar.viewChangeHistory')}
            title={t('topbar.viewChangeHistory')}
            className="shrink-0 whitespace-nowrap rounded text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            v{APP_VERSION}
          </button>
        </div>
      </div>

      {/* Page title, metadata, and page-level actions */}
      <div className="flex flex-1 items-baseline gap-3 overflow-hidden px-6">
        <h2 className="shrink-0 text-[26px] font-normal text-foreground">{pageTitle}</h2>
        {titleAddon && (
          <div className="shrink-0" style={{ transform: 'translateY(-3px)' }}>
            {titleAddon}
          </div>
        )}
        {isNotProd && (
          <Badge variant="info" className="shrink-0" style={{ transform: 'translateY(-3px)' }}>
            {t('topbar.nonProd')}
          </Badge>
        )}
      </div>

      <ChangeHistoryDialog open={changeHistoryOpen} onOpenChange={setChangeHistoryOpen} />
    </header>
  );
};
